import React from 'react';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CreateReportButton from '../../CreateReportButton';
import ImportReportButton from '../../ImportReportButton';
import SaveAsDialog from '../../components/SaveAsDialog';

// Hoisted static icon — stable reference across renders (rendering-hoist-jsx)
const moreVertIcon = <MoreVertIcon fontSize="small" />;

/**
 * Page header for the All Products view.
 *
 * Owns local UI state for the ⋮ kebab menu and the Rename dialog.
 * All data mutations are delegated upward via callbacks.
 *
 * Props:
 *   pageTitle            - displayed heading string
 *   isDirty              - whether filter rows differ from baseline
 *   activeSavedFilterId  - id of the active saved filter, or null
 *   activeSavedFilter    - full SavedFilter object, or null
 *   isPending            - useTransition pending flag (shows spinner on Save)
 *   user                 - current user session object
 *   filtersBar           - rendered filter bar node (from useFiltersRow)
 *   onUpdateSavedFilter  - called when "Save" is clicked (update existing filter)
 *   onOpenSaveAs         - called when "Save as" is clicked
 *   onDeleteFilter       - called when "Delete" is clicked from the ⋮ menu
 *   renameFilter(id, name) - persists a filter rename
 *   maxFilterNameLength    - character limit for filter names
 */
const PageHeader = ({
    pageTitle,
    isDirty,
    activeSavedFilterId,
    activeSavedFilter,
    isPending,
    user,
    filtersBar,
    maxFilterNameLength,
    onUpdateSavedFilter,
    onOpenSaveAs,
    onDeleteFilter,
    renameFilter,
    viewerMode,
}) => {
    const [headerMenuAnchor, setHeaderMenuAnchor] = React.useState(null);
    const [renameOpen, setRenameOpen] = React.useState(false);

    const handleRenameConfirm = React.useCallback((name) => {
        setRenameOpen(false);
        renameFilter(activeSavedFilterId, name);
    }, [renameFilter, activeSavedFilterId]);

    return (
        <>
            <Stack p={2} spacing={1}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={4}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                        <Typography variant="h5">
                            {pageTitle}
                        </Typography>
                        {isDirty && (activeSavedFilterId
                            ? (
                                <Button
                                    size="small"
                                    variant="outlined"
                                    disabled={isPending}
                                    startIcon={isPending ? <CircularProgress size={14} /> : undefined}
                                    onClick={onUpdateSavedFilter}
                                >
                                    {isPending ? 'Saving…' : 'Save'}
                                </Button>
                            ) : (
                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={onOpenSaveAs}
                                >
                                    Save as
                                </Button>
                            )
                        )}
                    </Stack>
                    <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                        {activeSavedFilterId && (
                            <IconButton size="small" onClick={(e) => setHeaderMenuAnchor(e.currentTarget)}>
                                {moreVertIcon}
                            </IconButton>
                        )}
                        {!viewerMode && <ImportReportButton user={user} />}
                        {!viewerMode && <CreateReportButton user={user} />}
                    </Stack>
                </Stack>

                {filtersBar}
            </Stack>

            {/* ⋮ menu — actions for the active saved filter; rendered as portal, safe outside Stack */}
            <Menu
                anchorEl={headerMenuAnchor}
                open={Boolean(headerMenuAnchor)}
                onClose={() => setHeaderMenuAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <MenuItem onClick={() => { setHeaderMenuAnchor(null); setRenameOpen(true); }}>
                    Rename
                </MenuItem>
                <MenuItem
                    sx={{ color: 'error.main' }}
                    onClick={() => { setHeaderMenuAnchor(null); onDeleteFilter(); }}
                >
                    Delete
                </MenuItem>
            </Menu>

            {/* Rename dialog — opened from the header ⋮ menu */}
            <SaveAsDialog
                open={renameOpen}
                title="Rename filter"
                initialName={activeSavedFilter?.name ?? ''}
                saving={isPending}
                maxLength={maxFilterNameLength}
                onClose={() => setRenameOpen(false)}
                onConfirm={handleRenameConfirm}
            />
        </>
    );
};

export default PageHeader;
